import type { ClientProcessDto } from '@nsb/core';
import { useMemo } from 'react';
import { homeSection } from '../homeWords.ts';
import { clientTiles } from '../tree/client.ts';
import { TILE_H, TILE_W, tileBox } from '../tree/layout.ts';
import { Tile } from '../tree/Tile.tsx';
import type { TileStep } from '../tree/model.ts';
import { SectionTitle } from '../ui.tsx';
import { towardWords, workingOn } from '../workingOn.ts';

/**
 * Working on, under the funnel on both homes (design study 31): the steps under way and the ones ready to start (paler)
 * as the board's own tiles, a drawn arrow, "Working toward", and the next unfinished gate or milestone with what it
 * closes, its day and what is left of its phase; no sentence over them (L-32: "we don't need this extra text"), only one
 * saying so where there is nothing to draw. Two across with the arrow pointing down on a phone. A tile
 * pressed opens that step in the Project process tree under it (`onOpen`).
 */
export function WorkingOnSection({ dto, onOpen }: { dto: ClientProcessDto; onOpen: (stepId: string) => void }) {
  const w = useMemo(() => workingOn(dto), [dto]);
  const tiles = useMemo(() => new Map(clientTiles(dto).steps.map((t) => [t.key, t])), [dto]);
  const shown = (ids: string[]) => ids.flatMap((id) => (tiles.has(id) ? [tiles.get(id)!] : []));
  const under = shown(w.under);
  const ready = shown(w.ready);
  const toward = w.toward ? (tiles.get(w.toward.id) ?? null) : null;
  return (
    <section className="mt-14">
      <SectionTitle>{homeSection('working')}</SectionTitle>
      {under.length || ready.length || toward ? null : <p className="max-w-2xl text-ink-2">Nothing is under way or ready to start.</p>}
      {under.length || ready.length || toward ? (
        // A row only where it has room (three tiles, the arrow and the mark want about 840px); stacked, the arrow down.
        <div className="mt-6 flex flex-col items-center gap-6 lg:flex-row lg:items-start">
          {under.length || ready.length ? (
            // As many across as there are tiles, three at most: never empty columns between the tiles and the arrow.
            <ul className="grid grid-cols-[repeat(2,152px)] gap-4 sm:grid-cols-[repeat(var(--across),152px)]" style={{ '--across': Math.min(3, under.length + ready.length) } as React.CSSProperties}>
              {under.map((t) => (
                <TileBox key={t.key} step={t} onOpen={onOpen} />
              ))}
              {ready.map((t) => (
                <TileBox key={t.key} step={t} muted onOpen={onOpen} />
              ))}
            </ul>
          ) : null}
          {toward && w.toward ? (
            <>
              {under.length || ready.length ? <Arrow /> : null}
              <div>
                <ul>
                  <TileBox step={toward} onOpen={onOpen} />
                </ul>
                <p className="mt-3 max-w-[12rem] text-[0.875rem] text-ink-2">{towardWords(w.toward)}</p>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * A tile as the board draws it, in a box of its own size (the board places tiles; here they sit in a grid): a task done
 * early's small tile at the box's foot, as in a row of the board (`tileBox`), opening its step (design study 31, slice 4).
 */
function TileBox({ step, muted = false, onOpen }: { step: TileStep; muted?: boolean; onOpen: (id: string) => void }) {
  const at = tileBox(step, 0, 0);
  return (
    <li className="relative" style={{ width: TILE_W, height: TILE_H }}>
      <Tile step={step} x={at.x} y={at.y} muted={muted} onOpen={() => onOpen(step.early?.stepKey ?? step.key)} />
    </li>
  );
}

/** Drawn, never a picture (guide §20.7): right across in a row, down where the section is stacked, its words over it. */
function Arrow() {
  return (
    // In a row, level with the middle of the first row of tiles.
    <div className="flex flex-col items-center gap-1.5 lg:mt-[62px]">
      <span className="label">Working toward</span>
      <svg width="150" height="30" viewBox="0 0 150 30" className="hidden lg:block" aria-hidden="true">
        <path d="M2 15 H128" stroke="currentColor" strokeWidth="3" />
        <path d="M126 4 L148 15 L126 26 Z" fill="currentColor" />
      </svg>
      <svg width="30" height="64" viewBox="0 0 30 64" className="lg:hidden" aria-hidden="true">
        <path d="M15 2 V44" stroke="currentColor" strokeWidth="3" />
        <path d="M4 42 L15 62 L26 42 Z" fill="currentColor" />
      </svg>
    </div>
  );
}
